import React, { useState, useEffect } from 'react';

const LAST_STATUS_API = '/api/device-status';

const STYLES = `
  @keyframes fadeUp  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes ringIn  { from{stroke-dasharray:0 999} }
  .bh-card { transition:box-shadow .2s,transform .18s; }
  .bh-card:hover { box-shadow:0 10px 36px rgba(0,0,0,.1)!important; transform:translateY(-2px); }
`;

function getBatteryStatus(installDateStr) {
  if (!installDateStr) return { label:'Unknown', color:'#8E8E93', years:null, pct:null, health:null };
  const d = new Date(installDateStr);
  if (isNaN(d)) return { label:'Unknown', color:'#8E8E93', years:null, pct:null, health:null };
  const years = (Date.now() - d.getTime()) / (365.25 * 86400000);
  // Health: 100% at 0yr → 0% at 4yr
  const health = Math.max(0, Math.min(100, Math.round(100 - years * 25)));
  // Age gauge: 0%=new, 100%=3yr+
  const pct = Math.min(100, Math.round((years / 3) * 100));
  if (years < 1) return { label:'Good',        color:'#34C759', years, pct, health };
  if (years < 2) return { label:'Aging',        color:'#FF9F0A', years, pct, health };
  return               { label:'Replace Now',   color:'#FF3B30', years, pct, health };
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}

// ── Circular battery gauge ───────────────────────────────────────────────────
function CircleGauge({ pct, color, size=60 }) {
  const r    = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const fill = pct != null ? (pct / 100) * circ : 0;
  return (
    <div style={{ width:size, height:size, position:'relative', flexShrink:0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none"
          stroke="rgba(0,0,0,.07)" strokeWidth="5"/>
        {pct != null && (
          <circle cx={size/2} cy={size/2} r={r} fill="none"
            stroke={color} strokeWidth="5" strokeLinecap="round"
            strokeDasharray={`${fill} ${circ - fill}`}
            strokeDashoffset={circ * 0.25}
            style={{ transition:'stroke-dasharray .9s ease' }}/>
        )}
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center', gap:0 }}>
        <div style={{ fontSize:11, fontWeight:900, color, lineHeight:1 }}>
          {pct != null ? `${pct}%` : '—'}
        </div>
        <div style={{ fontSize:7, color:'#8E8E93', fontWeight:600, marginTop:1 }}>AGE</div>
      </div>
    </div>
  );
}

// ── Health bar ───────────────────────────────────────────────────────────────
function HealthBar({ health, color }) {
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#8E8E93', marginBottom:4 }}>
        <span style={{ fontWeight:600 }}>Battery Health</span>
        <span style={{ color, fontWeight:800 }}>{health}%</span>
      </div>
      <div style={{ background:'rgba(0,0,0,.06)', borderRadius:999, height:8, overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:999,
          background:`linear-gradient(90deg,${color},${color}99)`,
          width:`${health}%`, transition:'width .8s ease',
          boxShadow:`0 0 10px ${color}44` }}/>
      </div>
    </div>
  );
}

function KPICard({ label, value, color, icon, sub, delay='0s' }) {
  return (
    <div style={{
      background:'#fff', borderRadius:16, padding:'16px 18px',
      border:`1.5px solid ${color}22`, boxShadow:'0 2px 10px rgba(0,0,0,.05)',
      position:'relative', overflow:'hidden',
      animation:`fadeUp .35s ease ${delay} both`,
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background:color, borderRadius:'16px 16px 0 0' }}/>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div style={{ fontSize:10, fontWeight:700, color:'#8E8E93', letterSpacing:.4, textTransform:'uppercase' }}>{label}</div>
        <span style={{ fontSize:20 }}>{icon}</span>
      </div>
      <div style={{ fontSize:30, fontWeight:900, color:'#1c1c1e', letterSpacing:-.8, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color, fontWeight:600, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function DeviceCard({ device, idx }) {
  const reg    = device._registration || {};
  const status = getBatteryStatus(device._installDate);

  return (
    <div className="bh-card" style={{
      background:'#fff', borderRadius:18, padding:'20px',
      border:`1.5px solid ${status.color}18`,
      boxShadow:'0 2px 14px rgba(0,0,0,.06)',
      animation:`fadeUp .35s ease ${idx*.07}s both`,
      position:'relative', overflow:'hidden',
    }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:3,
        background:`linear-gradient(90deg,${status.color},${status.color}55)`,
        borderRadius:'18px 18px 0 0' }}/>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <CircleGauge pct={status.pct} color={status.color} size={58}/>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:'#1c1c1e', lineHeight:1.2 }}>
              {device.friendlyname || 'Unknown Device'}
            </div>
            <div style={{ fontSize:10, color:'#8E8E93', fontFamily:'monospace', marginTop:3 }}>
              {device.macid?.toUpperCase() || '—'}
            </div>
          </div>
        </div>
        <span style={{ fontSize:11, fontWeight:800, padding:'5px 12px', borderRadius:20,
          background:`${status.color}15`, color:status.color, letterSpacing:.4,
          boxShadow:`0 2px 8px ${status.color}22` }}>
          {status.label}
        </span>
      </div>

      {/* Health bar */}
      {status.health != null && (
        <div style={{ marginBottom:14 }}>
          <HealthBar health={status.health} color={status.color}/>
        </div>
      )}

      {/* Age bar */}
      {status.years != null && (
        <div style={{ marginBottom:16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:10,
            color:'#8E8E93', marginBottom:5 }}>
            <span style={{ fontWeight:600 }}>Battery Age</span>
            <span style={{ color:status.color, fontWeight:700 }}>
              {status.years < 1
                ? `${Math.round(status.years*12)} months`
                : `${status.years.toFixed(1)} years`}
            </span>
          </div>
          <div style={{ background:'rgba(0,0,0,.06)', borderRadius:999, height:6, overflow:'hidden' }}>
            <div style={{ height:'100%', borderRadius:999,
              background:`linear-gradient(90deg,#34C759 0%,#FF9F0A 33%,#FF3B30 66%)`,
              width:`${Math.min(100,(status.years/3)*100)}%`, transition:'width .8s ease' }}/>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:3 }}>
            {['0yr','1yr','2yr','3yr+'].map(l => (
              <span key={l} style={{ fontSize:8, color:'#C7C7CC' }}>{l}</span>
            ))}
          </div>
        </div>
      )}

      {/* Info grid */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        {[
          ['Install Date',   formatDate(device._installDate)],
          ['Status',         status.label],
          ['Customer',       reg.userName || reg.custName || '—'],
          ['Phone',          reg.phone || '—'],
          ['Location',       reg.location || '—'],
          ['Model',          reg.modelNo || '—'],
        ].map(([label, value]) => (
          <div key={label} style={{ padding:'9px 12px', background:'rgba(0,0,0,.025)', borderRadius:11 }}>
            <div style={{ fontSize:9, color:'#8E8E93', fontWeight:700, letterSpacing:.3, textTransform:'uppercase', marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:12, color:'#1c1c1e', fontWeight:700, wordBreak:'break-word' }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BatteryHealthPage({ devices }) {
  const [enriched, setEnriched] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!devices || devices.length === 0) { setLoading(false); return; }
    setLoading(true);
    const promises = devices.map(dev => {
      const mac = (dev.macid||'').replace(/[^a-fA-F0-9]/g,'').toLowerCase();
      if (!mac) return Promise.resolve({ ...dev, _registration:{}, _installDate:null });
      return fetch(`${LAST_STATUS_API}?macid=${mac}`)
        .then(r => r.ok ? r.json() : {})
        .then(json => {
          const reg = json.registration || {};
          const installDate = reg.install_date || reg.created_at || reg.registration_date
            || json.latest_recharge?.data?.T || null;
          return { ...dev,
            _registration: { userName:reg.Enduser_name||reg.cust_name||'', phone:reg.phone_no||'',
              location:reg.location||'', modelNo:reg.model_no||'' },
            _installDate: installDate };
        })
        .catch(() => ({ ...dev, _registration:{}, _installDate:null }));
    });
    Promise.all(promises).then(r => { setEnriched(r); setLoading(false); });
  }, [devices]);

  const filtered = enriched.filter(d => {
    const reg = d._registration||{};
    return !!(d._installDate||d.friendlyname||reg.userName||reg.location||reg.modelNo);
  });
  const total   = filtered.length;
  const good    = filtered.filter(d => getBatteryStatus(d._installDate).label==='Good').length;
  const aging   = filtered.filter(d => getBatteryStatus(d._installDate).label==='Aging').length;
  const replace = filtered.filter(d => getBatteryStatus(d._installDate).label==='Replace Now').length;

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',sans-serif", maxWidth:960 }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <img src="/havells-logo.png" alt="Havells" style={{ height:40, objectFit:'contain', marginBottom:10, display:'block' }}/>
        <h1 style={{ fontSize:26, fontWeight:800, color:'#1c1c1e', letterSpacing:-.5, margin:0 }}>
          🔋 Battery Health
        </h1>
        <p style={{ color:'#8E8E93', fontSize:13, marginTop:4, margin:'4px 0 0' }}>
          Battery age & replacement planning · {total} device{total!==1?'s':''} tracked
          {replace > 0 && <span style={{ color:'#FF3B30', fontWeight:700 }}> · {replace} need replacement</span>}
        </p>
      </div>

      {/* KPI cards — 2 only */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24, maxWidth:400 }}>
        <KPICard label="Total Devices" value={total} color="#007AFF" icon="📊" delay=".0s"/>
        <KPICard label="0 – 2 Yrs"     value={total} color="#34C759" icon="🔋" sub="Active devices" delay=".05s"/>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'#8E8E93' }}>
          <div style={{ width:28,height:28,borderRadius:'50%',border:'3px solid rgba(255,159,10,.15)',
            borderTopColor:'#FF9F0A',animation:'spin .7s linear infinite',margin:'0 auto 14px'}}/>
          <p style={{ fontSize:13 }}>Loading device data…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background:'#fff',borderRadius:20,padding:'56px 24px',textAlign:'center',
          border:'1px solid rgba(0,0,0,.06)',boxShadow:'0 2px 10px rgba(0,0,0,.04)' }}>
          <div style={{ fontSize:48, marginBottom:14 }}>🔋</div>
          <div style={{ fontSize:16, fontWeight:700, color:'#1c1c1e', marginBottom:6 }}>No devices found</div>
          <div style={{ fontSize:13, color:'#8E8E93' }}>No registered devices with battery data</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(420px,1fr))', gap:14 }}>
          {filtered.map((dev, idx) => (
            <DeviceCard key={dev.macid||idx} device={dev} idx={idx}/>
          ))}
        </div>
      )}
    </div>
  );
}
